import { logger } from "../services/logger.ts";
import React, { useState, useEffect, useCallback, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Star, Award, Sparkles } from 'lucide-react';
import { Market, MarketOption } from "../types.ts";
import { supabase } from "../lib/db.ts";
import { useAuth } from "../lib/auth.tsx";
import { MarketEngine, MarketDataFromDb } from "../lib/marketEngine.ts";
import toast from 'react-hot-toast';
import { useResolutionCountdown } from '../hooks/useResolutionCountdown.ts';

// Initialize MarketEngine
const marketEngine = new MarketEngine();

interface MarketCardProps {
  market: Market;
  isAdmin?: boolean;
  onWatchlistChange?: (marketId: string, isWatchlisted: boolean) => void;
}

// Memoized MarketCard component to prevent unnecessary re-renders
const MarketCard = memo(function MarketCard({ market, isAdmin = false, onWatchlistChange }: MarketCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isWatchlisted, setIsWatchlisted] = useState(market.is_watchlisted || false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isFeatured, setIsFeatured] = useState(market.is_featured || false);
  const [isUpdatingFeatured, setIsUpdatingFeatured] = useState(false);
  
  // Memoized calculations
  const marketOptions = React.useMemo(() => 
    Array.isArray(market.market_options) ? market.market_options : [], 
    [market.market_options]
  );
  
  const winningOption = React.useMemo(() => 
    market.status === 'resolved' && market.winning_option_id
      ? marketOptions.find(option => option.id === market.winning_option_id)
      : null,
    [market.status, market.winning_option_id, marketOptions]
  );

  const sortedOptions = React.useMemo(() => 
    marketOptions.length > 0 
      ? [...marketOptions].sort((a, b) => (b.probability ?? 0) - (a.probability ?? 0))
      : [],
    [marketOptions]
  );

  const displayOptions = React.useMemo(() => sortedOptions.slice(0, 3), [sortedOptions]);

  const winningOptionPricing = React.useMemo(() => {
    if (!winningOption) return null;
    
    try {
      const pricingMarketData: MarketDataFromDb = {
        id: market.id,
        title: market.title,
        is_private: market.is_private,
        category: market.category,
        description: market.description,
        created_by: market.created_by,
        status: market.status,
        total_bets: market.total_bets,
        market_options: market.market_options,
        market_pools: market.marketPools,
        is_dynamic: market.is_dynamic,
        engine_type: market.engine_type,
        lmsr_b: market.lmsr_b,
      };
      return marketEngine.calculatePricing(pricingMarketData, winningOption.id);
    } catch (error) {
      logger.error(`Error calculating pricing for winning option ${winningOption.id}:`, error);
      return null;
    }
  }, [winningOption, market]);

  const marketDetailRoute = React.useMemo(() => 
    isAdmin ? `/admin/market/${market.id}` : `/markets/${market.id}`,
    [isAdmin, market.id]
  );

  const imageUrl = React.useMemo(() => 
    market.image_url || '',
    [market.image_url]
  );

  const resolutionCountdown = useResolutionCountdown(market.resolution_time, {
    enabled: market.status === 'active',
  });

  // Optimized format percentage function
  const formatPercentage = useCallback((value: number | undefined): string => {
    if (value === undefined || value === null) return 'N/A';
    return `${value.toFixed(1)}%`;
  }, []);

  // Update local state when market prop changes
  useEffect(() => {
    if (market.is_watchlisted !== undefined) {
      setIsWatchlisted(market.is_watchlisted);
    }
  }, [market.is_watchlisted]);

  // Update featured state when market prop changes
  useEffect(() => {
    if (market.is_featured !== undefined) {
      setIsFeatured(market.is_featured);
    }
  }, [market.is_featured]);

  // Optimized watchlist status fetching
  useEffect(() => {
    if (!user || !market.id) return;

    let isMounted = true;
    
    const fetchWatchlistStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('watchlists')
          .select('id')
          .eq('user_id', user.id)
          .eq('market_id', market.id)
          .maybeSingle();
          
        if (!error && isMounted) {
          const isInWatchlist = !!data;
          setIsWatchlisted(isInWatchlist);
        }
      } catch (error) {
        logger.error('Error fetching watchlist status:', error);
      }
    };
    
    fetchWatchlistStatus();
    
    return () => {
      isMounted = false;
    };
  }, [user, market.id]);

  // Optimized watchlist toggle handler
  const handleToggleWatchlist = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      navigate('/login', { state: { from: marketDetailRoute } });
      return;
    }
    
    if (isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      const { error } = await supabase.rpc('toggle_watchlist', {
        p_market_id: market.id
      });
      
      if (!error) {
        const newWatchlistState = !isWatchlisted;
        setIsWatchlisted(newWatchlistState);
        onWatchlistChange?.(market.id, newWatchlistState);
      } else {
        logger.error('Error toggling watchlist:', error);
      }
    } catch (error) {
      logger.error('Exception when toggling watchlist:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [user, navigate, marketDetailRoute, isUpdating, isWatchlisted, market.id, onWatchlistChange]);

  // Toggle featured status handler (admin only)
  const handleToggleFeatured = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAdmin || isUpdatingFeatured) return;
    
    setIsUpdatingFeatured(true);
    const newFeaturedState = !isFeatured;
    
    try {
      const { error } = await supabase.rpc('toggle_market_featured', {
        p_market_id: market.id,
        p_is_featured: newFeaturedState
      });
      
      if (!error) {
        setIsFeatured(newFeaturedState);
      } else {
        logger.error('Error toggling featured status:', error);
        toast.error('Failed to update featured status. Please try again.', {
          duration: 4000,
        });
      }
    } catch (error) {
      logger.error('Exception when toggling featured status:', error);
    } finally {
      setIsUpdatingFeatured(false);
    }
  }, [isAdmin, isUpdatingFeatured, isFeatured, market.id]);

  // Early return for resolved markets
  if (market.status === 'resolved' && winningOption) {
    return (
      <Link to={marketDetailRoute} className="block group h-full min-h-0">
        <div 
        className="rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 flex flex-col h-full min-h-[188px] border border-gray-100 hover:border-wayja-teal/30"
        style={{ backgroundColor: 'rgba(92, 179, 173, 0.12)' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(92, 179, 173, 0.18)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(92, 179, 173, 0.12)'}
      >
          {/* Resolved market UI */}
          <div className="flex-shrink-0 p-2.5">
            <div className="flex items-start gap-2.5">
              <div className="relative w-10 h-10 bg-gray-200 overflow-hidden rounded flex-shrink-0">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={market.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                    <span className="text-gray-400 text-xs font-medium">{market.category}</span>
                  </div>
                )}
              </div>

              {/* Header content area */}
              <div className="flex-1 min-w-0">
                {/* Status badges */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex flex-wrap gap-1">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                      <span className="w-1 h-1 rounded-full mr-1 bg-blue-500"></span>
                      Resolved
                    </span>
                  </div>

                  {/* Featured toggle button (admin only) */}
                  {isAdmin && (
                    <button
                      onClick={handleToggleFeatured}
                      disabled={isUpdatingFeatured}
                      className={`p-1 rounded-full shadow-sm transition-colors flex-shrink-0 ${
                        isFeatured 
                          ? 'bg-amber-100 hover:bg-amber-200' 
                          : 'bg-white/80 hover:bg-white'
                      }`}
                      title={isFeatured ? 'Remove from featured' : 'Add to featured'}
                    >
                      <Sparkles
                        className={`w-3.5 h-3.5 ${
                          isUpdatingFeatured 
                            ? 'animate-pulse text-gray-400' 
                            : isFeatured 
                              ? 'fill-amber-500 text-amber-500' 
                              : 'text-gray-400'
                        }`}
                      />
                    </button>
                  )}

{/* Watchlist button */}
                <button
                  onClick={handleToggleWatchlist}
                  disabled={isUpdating}
                  className="p-1 rounded-full bg-white/80 hover:bg-white shadow-sm transition-colors flex-shrink-0"
                  title={isWatchlisted ? 'Remove from watchlist' : 'Add to watchlist'}
                >
                  <Star
                    className={`w-3.5 h-3.5 ${isWatchlisted ? 'fill-wayja-teal text-wayja-teal' : 'text-gray-400'}`}
                  />
                </button>
              </div>

              <h3 className="font-bold text-xs group-hover:text-wayja-teal transition-colors line-clamp-2 leading-snug h-[2.6em]">{market.title}</h3>
            </div>
          </div>
          </div>
          
            <div className="mt-2 flex flex-1 flex-col items-center justify-center text-center p-2.5 min-h-0 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-t-2 border-blue-200">
              <div className="mb-2 p-1.5 bg-blue-200 rounded-full">
                <Award className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-[10px] font-medium text-blue-700 uppercase tracking-wide mb-0.5">Winning Outcome</p>
              <p className="text-sm font-bold text-gray-800 leading-tight mb-1.5 px-2 line-clamp-2" title={winningOption.name}>
                {winningOption.name}
              </p>
              {winningOptionPricing ? (
                <div className="flex flex-col items-center space-y-0.5 text-[10px] text-gray-600">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">{winningOptionPricing.yesPercentage.toFixed(1)}%</span>
                    <span className="text-gray-400">Probability</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium">
                      {(winningOptionPricing.yesPercentage === 0 || winningOptionPricing.odds === Number.MAX_SAFE_INTEGER)
                        ? '—'
                        : `${winningOptionPricing.odds.toFixed(2)}x`
                      }
                    </span>
                    <span className="text-gray-400">Return</span>
                  </div>
                </div>
              ) : (
                <p className="text-[10px] text-gray-500 mt-0.5 italic">Pricing unavailable</p>
              )}
            </div>
        </div>
      </Link>
    );
  }

  // Regular market card UI (active, ended, etc.)
  return (
    <Link to={marketDetailRoute} className="block group h-full min-h-0">
      <div 
        className="relative h-full min-h-[188px] rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 flex flex-col border border-gray-100 hover:border-wayja-teal/30"
        style={{ backgroundColor: 'rgba(92, 179, 173, 0.12)' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(92, 179, 173, 0.18)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(92, 179, 173, 0.12)'}
      >
        {/* Header with image and badges */}
        <div className="flex flex-1 flex-col min-h-0 p-3 pb-4">
          <div className="flex items-start gap-2.5">
            <div className="relative w-11 h-11 bg-gray-200 overflow-hidden rounded flex-shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={market.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                <span className="text-gray-400 text-[10px] font-medium px-0.5 text-center line-clamp-2 leading-tight">{market.category}</span>
              </div>
            )}
            </div>

            {/* Header content area */}
            <div className="flex-1 min-w-0">
              {/* Status, category, time — action buttons */}
              <div className="flex items-center justify-between gap-1 mb-1.5 min-h-[1.375rem]">
                <div className="flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                  {market.status === 'active' && (
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      resolutionCountdown === 'Ended'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      <span className={`w-1 h-1 rounded-full mr-1 ${
                        resolutionCountdown === 'Ended'
                          ? 'bg-wayja-teal'
                          : 'bg-green-500'
                      }`}></span>
                      {resolutionCountdown === 'Ended' ? 'Ended' : 'Active'}
                    </span>
                  )}
                  {market.status !== 'active' && market.status !== 'resolved' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700 capitalize">
                      {market.status.replace(/_/g, ' ')}
                    </span>
                  )}
                  <span className="inline-flex items-center bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm font-medium text-[10px] max-w-[42%] truncate" title={market.category}>
                    {market.category}
                  </span>
                  <div className="flex flex-col items-end min-w-0 shrink mr-1 text-right leading-tight">
                    <span className="text-[9px] text-gray-400 uppercase tracking-wide hidden sm:block">Market resolution</span>
                    <span className="inline-flex items-center justify-end text-[10px] text-gray-500 min-w-0 mt-px">
                      <Clock className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" />
                      <span className="truncate">{resolutionCountdown}</span>
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-0 flex-shrink-0">
                  {isAdmin && (
                    <button
                      onClick={handleToggleFeatured}
                      disabled={isUpdatingFeatured}
                      className={`p-1 rounded-full shadow-sm transition-colors ${
                        isFeatured 
                          ? 'bg-amber-100 hover:bg-amber-200' 
                          : 'bg-white/80 hover:bg-white'
                      }`}
                      title={isFeatured ? 'Remove from featured' : 'Add to featured'}
                    >
                      <Sparkles
                        className={`w-3.5 h-3.5 ${
                          isUpdatingFeatured 
                            ? 'animate-pulse text-gray-400' 
                            : isFeatured 
                              ? 'fill-amber-500 text-amber-500' 
                              : 'text-gray-400'
                        }`}
                      />
                    </button>
                  )}

                  <button
                    onClick={handleToggleWatchlist}
                    disabled={isUpdating}
                    className="p-1 rounded-full bg-white/80 hover:bg-white shadow-sm transition-colors"
                    title={isWatchlisted ? 'Remove from watchlist' : 'Add to watchlist'}
                  >
                    <Star
                      className={`w-3.5 h-3.5 ${isWatchlisted ? 'fill-wayja-teal text-wayja-teal' : 'text-gray-400'}`}
                    />
                  </button>
                </div>
              </div>

              <h3 className="font-bold text-xs group-hover:text-wayja-teal transition-colors line-clamp-2 leading-snug h-[2.6em]">{market.title}</h3>
            </div>
          </div>

          {/* Preview up to 3 outcomes; grid stays even via fixed bar row height */}
          <div className="min-h-[52px] shrink-0 mt-3 mb-1 pr-1">
            <div className="space-y-2 pr-1">
              {displayOptions.map((option: MarketOption, index: number) => (
                <div key={option.id || index} className="flex items-center">
                  <div className="w-full bg-gray-100 group-hover:bg-white rounded-full h-[1.35rem] relative overflow-hidden transition-colors">
                    <div 
                      className="h-full rounded-full transition-all duration-500 ease-in-out bg-wayja-teal/40 group-hover:bg-wayja-teal"
                      style={{ 
                        width: `${option.probability ?? 0}%`
                      }}
                    ></div>
                    <div className="absolute inset-0 px-2 flex justify-between items-center">
                      <span className="text-[11px] font-medium text-gray-800 truncate max-w-[65%] flex items-center min-w-0" title={option.name}>
                        {option.image_url ? (
                          <img
                            src={option.image_url}
                            alt=""
                            className="w-6 h-6 rounded-full object-cover mr-2 shrink-0"
                          />
                        ) : null}
                        <span className="truncate">{option.name}</span>
                      </span>
                      <div className="flex items-center space-x-1">
                        <span className="text-[11px] font-semibold text-gray-700">{formatPercentage(option.probability)}</span>
                        <span className="text-[11px] font-semibold text-gray-600">
                          {option.odds && option.odds > 1000 ? '∞' : option.odds?.toFixed(2) + 'x'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {marketOptions.length === 0 && (
                <div className="text-xs text-gray-500 text-center min-h-[52px] flex items-center justify-center">
                  No options available
                </div>
              )}
            </div>
            {sortedOptions.length > 3 && (
              <p className="text-[10px] text-gray-500 mt-1.5 pl-0.5">
                + {sortedOptions.length - 3} more options
              </p>
            )}
          </div>

          <p className="pointer-events-none absolute bottom-1.5 left-3 text-[9px] text-gray-500 leading-tight text-left max-w-[75%]">
            Market volume{' '}
            <span className="font-semibold text-gray-600">
              R{market.totalPool ? market.totalPool.toLocaleString('en-US', { maximumFractionDigits: 0 }) : 0}
            </span>
          </p>
        </div>
      </div>
    </Link>
  );
});

export { MarketCard };